import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { round } from "lodash";
import { InputBoolean } from "ng-zorro-antd/core";
import { NzTableComponent } from "ng-zorro-antd/table";
import { Page } from "../model/common-type";
import { TotalOption } from "../hd-detail-lines/hd-detail-lines.service";
import {
  measureTableColWidth,
  resolveAutoTableScroll,
  bindNzTableHeaderBodyScrollSync,
} from "../utils/hd-table-scroll.util";

@Component({
  selector: "hd-detail-lines-page",
  templateUrl: "./hd-detail-lines-page.component.html",
  styleUrls: ["./hd-detail-lines-page.component.less"],
})
export class HdDetailLinesPageComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild("hdTable", { static: false }) nzTableComponent: NzTableComponent;
  @Input() lines: Array<any> = [];
  /** 与 hd-table 一致，可传 Page 对象；未传 lines 时使用 tableData.content */
  @Input() tableData: Page<any> = new Page();
  @Input() @InputBoolean() showSelected: boolean = false;
  @Input() @InputBoolean() allowDrop: boolean = false;
  /** 与 hd-table 一致：行数多时自动虚拟滚动；拖拽排序开启时强制关闭 */
  @Input() @InputBoolean() virtualScroll: boolean = true;
  /** 手动指定行高；不传则用默认/测量值 */
  @Input() virtualItemSize: number;
  /**
   * 横向滚动。历史默认 `{ x: '1px' }`（2.1.28 前一直如此）：开启横滚但不锁死列宽，
   * 业务页可不写 th/td width。传 null 则按容器自动测算。
   */
  @Input() scroll: { [key: string]: string } = { x: "1px" };
  tableScroll: { x?: string; y?: string } = null;
  @Input() selectField: string = "billNumber";
  @Input() selectedField: string;
  /**
   * 行勾选禁用字段名。定义后取当前行该字段：
   * 为 true 时禁用该行勾选（全选也会跳过）。
   */
  @Input() selectDisabledField: string;
  /**
   * 行失效字段名。定义后取当前行该字段：
   * 为 true 时整行背景色为 #F7F8FA、文字 #868A9C。
   */
  @Input() rowInvalidField: string;
  @Input() isCrossPageSelect: boolean;
  @Output() selectEvent = new EventEmitter();
  @ContentChild("detailLineHead", { static: false })
  detailLineHead: TemplateRef<any>;
  @ContentChild("detailLineBody", { static: false })
  detailLineBody: TemplateRef<any>;
  @Input() @InputBoolean() showTotal: boolean = false;
  @Input() totalOption: Array<TotalOption> = [];
  @Input() columnsNumber: number | string = 0;
  @Input() tableLoading: boolean = false;
  @Input() tablePageIndex: number = 1;
  @Input() tablePageSize: number = 100;
  @Output() tablePageIndexChange = new EventEmitter<number>();
  @Output() tablePageSizeChange = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<void>();

  totalOptionList = [];
  isAllDisplayDataChecked = false;
  isIndeterminate = false;
  listOfDisplayData = [];
  mapOfCheckedId: { [key: string]: boolean } = {};
  mapOfCheckList: { [key: string]: any } = {};
  isCrossPageSelectLocal: boolean = false;

  showTable = true;
  /** 自动测量前的兜底行高 */
  measuredVirtualItemSize = 47;
  private virtualScrollActive: boolean | null = null;
  private unbindBodyScroll: (() => void) | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get effectiveScroll(): { x?: string; y?: string } {
    return this.scroll != null ? this.scroll : this.tableScroll;
  }

  get effectiveVirtualItemSize(): number {
    if (this.virtualItemSize != null && this.virtualItemSize > 0) {
      return this.virtualItemSize;
    }
    return this.measuredVirtualItemSize;
  }

  /** 当前页内容高度是否超出视口 / 已指定 scroll.y */
  get needsVerticalScroll(): boolean {
    const rowCount = this.displayLines ? this.displayLines.length : 0;
    if (rowCount <= 0) {
      return false;
    }
    const base = this.effectiveScroll;
    if (base && base.y) {
      // 父级已定高（如弹窗），行稍多就开虚拟，避免千行 DOM
      return rowCount > 20;
    }
    const maxOffset = 200;
    const estimatedHeight = rowCount * this.effectiveVirtualItemSize;
    if (typeof window !== "undefined") {
      return estimatedHeight > window.innerHeight - maxOffset;
    }
    return rowCount > 100;
  }

  get useVirtualScroll(): boolean {
    if (!this.virtualScroll || this.allowDrop) {
      return false;
    }
    return this.needsVerticalScroll;
  }

  /** 虚拟滚动必须有 y；业务自适应列宽场景不自动给仅有 x 的表补 y */
  get resolvedScroll(): { x?: string; y?: string } {
    const base = this.effectiveScroll ? { ...this.effectiveScroll } : {};
    if (this.useVirtualScroll && !base.y) {
      base.y = this.getAutoScrollY();
    }
    return base;
  }

  /** 虚拟滚动按数组下标追踪，避免业务主键 trackBy 导致 CDK 行复用错位 */
  virtualTrackByRow = (index: number): number => index;

  ngOnInit(): void {
    if (this.isCrossPageSelect === true) {
      this.isCrossPageSelectLocal = this.isCrossPageSelect;
    }
    this.countSelected();
    this.calculateTotal();
  }

  ngAfterViewInit(): void {
    this.scheduleSyncHorizontalScroll();
    this.applyTableScrollMode(true);
  }

  ngOnDestroy(): void {
    this.unbindHeaderBodyScrollSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes["tableData"] &&
      changes["tableData"]["currentValue"] &&
      changes["tableData"]["previousValue"]
    ) {
      if (
        changes["tableData"]["currentValue"]["pageSize"] !==
          changes["tableData"]["previousValue"]["pageSize"] ||
        changes["tableData"]["currentValue"]["pageNumber"] !==
          changes["tableData"]["previousValue"]["pageNumber"]
      ) {
        this.countSelected();
        this.calculateTotal();
        this.applyTableScrollMode(false);
        return;
      }
      if (!this.isCrossPageSelectLocal) {
        this.resetStatus();
      }
    }
    this.countSelected();
    this.calculateTotal();
    if (
      changes["lines"] ||
      changes["tableData"] ||
      changes["showSelected"] ||
      changes["scroll"] ||
      changes["virtualScroll"] ||
      changes["allowDrop"]
    ) {
      this.scheduleSyncHorizontalScroll();
      this.applyTableScrollMode(false);
    }
  }

  /** 虚拟/普通模式切换时重建表格，避免残留滚动容器 */
  applyTableScrollMode(forceRemount = false): void {
    const useVirtual = this.useVirtualScroll;
    const modeChanged =
      this.virtualScrollActive !== null &&
      this.virtualScrollActive !== useVirtual;
    this.virtualScrollActive = useVirtual;

    if (forceRemount || modeChanged) {
      this.unbindHeaderBodyScrollSync();
      // 延后销毁/重建，避免当前 CD 周期内 ngIf 变更触发 ExpressionChanged
      setTimeout(() => {
        this.showTable = false;
        this.cdr.markForCheck();
        setTimeout(() => {
          this.showTable = true;
          this.cdr.markForCheck();
          setTimeout(() => {
            this.afterTableRender(useVirtual);
          });
        });
      });
      return;
    }

    this.cdr.markForCheck();
    this.afterTableRender(useVirtual);
  }

  private afterTableRender(useVirtual: boolean): void {
    this.scheduleSyncHorizontalScroll();
    this.bindHeaderBodyScrollSync(0);
    if (useVirtual) {
      this.refreshVirtualScrollViewport(true);
    }
  }

  refreshVirtualScrollViewport(forceMeasure = false): void {
    if (!this.useVirtualScroll) {
      this.bindHeaderBodyScrollSync(0);
      return;
    }
    setTimeout(() => {
      this.measureVirtualItemSize(forceMeasure);
      const viewport =
        this.nzTableComponent && this.nzTableComponent.cdkVirtualScrollViewport;
      if (viewport) {
        viewport.checkViewportSize();
      }
      this.bindHeaderBodyScrollSync(0);
      this.cdr.markForCheck();
    });
  }

  private measureVirtualItemSize(force = false): void {
    if (this.virtualItemSize != null && this.virtualItemSize > 0 && !force) {
      return;
    }
    const viewport =
      this.nzTableComponent && this.nzTableComponent.cdkVirtualScrollViewport;
    if (!viewport || !viewport.elementRef || !viewport.elementRef.nativeElement) {
      return;
    }
    const row = viewport.elementRef.nativeElement.querySelector(
      "tr.hd-detail-lines-page-virtual-row",
    ) as HTMLElement;
    if (row && row.offsetHeight > 0) {
      this.measuredVirtualItemSize = row.offsetHeight;
    }
  }

  private scheduleSyncHorizontalScroll(retry = 0): void {
    const delay = retry === 0 ? 0 : 50;
    setTimeout(() => {
      if (this.scroll == null) {
        this.syncHorizontalScroll();
      }
      this.bindHeaderBodyScrollSync(0);
      const afterX =
        (this.resolvedScroll && this.resolvedScroll.x) ||
        (this.tableScroll && this.tableScroll.x);
      if (
        this.scroll == null &&
        retry < 5 &&
        this.displayLines &&
        this.displayLines.length > 0 &&
        !afterX &&
        measureTableColWidth(this.nzTableComponent) === 0
      ) {
        this.scheduleSyncHorizontalScroll(retry + 1);
      }
    }, delay);
  }

  private getAutoScrollY(): string {
    return "calc(100vh - 280px)";
  }

  private syncHorizontalScroll(): void {
    if (this.scroll != null) {
      return;
    }
    const hasData = !!(this.displayLines && this.displayLines.length);
    const nextScroll = resolveAutoTableScroll(
      this.nzTableComponent,
      hasData,
    );
    const nextX = nextScroll ? nextScroll.x : null;
    // 自动测算只设 x；虚拟滚动才需要 y
    const nextY = this.useVirtualScroll ? this.getAutoScrollY() : null;
    const prevHasY = !!(this.tableScroll && this.tableScroll.y);
    const nextHasY = !!nextY;
    if (
      this.tableScroll &&
      this.tableScroll.x === nextX &&
      this.tableScroll.y === nextY
    ) {
      return;
    }
    this.tableScroll =
      nextX || nextY
        ? nextY
          ? { x: nextX, y: nextY }
          : { x: nextX }
        : null;
    if (prevHasY !== nextHasY) {
      this.applyTableScrollMode(true);
      return;
    }
    this.cdr.markForCheck();
    this.bindHeaderBodyScrollSync(0);
  }

  /** 表体横滚时同步表头；DOM 未就绪时重试 */
  private bindHeaderBodyScrollSync(retry = 0): void {
    this.unbindHeaderBodyScrollSync();
    if (!this.nzTableComponent || !(this.resolvedScroll && this.resolvedScroll.x)) {
      return;
    }
    const unbind = bindNzTableHeaderBodyScrollSync(this.nzTableComponent);
    if (unbind) {
      this.unbindBodyScroll = unbind;
      return;
    }
    if (retry < 8) {
      setTimeout(() => this.bindHeaderBodyScrollSync(retry + 1), 50);
    }
  }

  private unbindHeaderBodyScrollSync(): void {
    if (this.unbindBodyScroll) {
      this.unbindBodyScroll();
      this.unbindBodyScroll = null;
    }
  }

  get displayLines(): Array<any> {
    if (this.lines && this.lines.length) {
      return this.lines;
    }
    return (this.tableData && this.tableData.content) || [];
  }

  get totalCount(): number {
    if (this.tableData && this.tableData.totalElements != null) {
      return this.tableData.totalElements;
    }
    return this.displayLines.length;
  }

  getRowIndex(localIndex: number): number {
    return (this.tablePageIndex - 1) * this.tablePageSize + localIndex;
  }

  onPageIndexChange(pageIndex: number): void {
    this.tablePageIndex = pageIndex;
    this.tablePageIndexChange.emit(this.tablePageIndex);
    this.pageChange.emit();
    this.scheduleSyncHorizontalScroll();
    this.applyTableScrollMode(false);
  }

  onPageSizeChange(pageSize: number): void {
    this.tablePageSize = pageSize;
    this.tablePageIndex = 1;
    this.tablePageSizeChange.emit(this.tablePageSize);
    this.tablePageIndexChange.emit(this.tablePageIndex);
    this.pageChange.emit();
    this.scheduleSyncHorizontalScroll();
    this.applyTableScrollMode(true);
  }

  private countSelected(): void {
    if (
      this.showSelected &&
      this.selectedField &&
      this.displayLines &&
      this.displayLines.length
    ) {
      this.displayLines.forEach((item) => {
        if (item[this.selectedField] === true && !this.isSelectDisabled(item)) {
          this.mapOfCheckedId[item[this.selectField]] = true;
          this.mapOfCheckList[item[this.selectField]] = item;
        }
      });
      if (
        this.displayLines.length === Object.keys(this.mapOfCheckList).length
      ) {
        this.isAllDisplayDataChecked = true;
        this.isIndeterminate = true;
      }
    }
  }

  get LodashRound() {
    return round;
  }

  drop(event: CdkDragDrop<string[]>): void {
    const lines = [...this.displayLines];
    moveItemInArray(lines, event.previousIndex, event.currentIndex);
    this.lines = lines;
  }

  calculateTotal() {
    if (this.showTotal && this.displayLines && this.displayLines.length) {
      this.totalOptionList = new Array(Number(this.columnsNumber) - 1).fill("");
      this.totalOption.forEach((item) => {
        if (item.insertIndex && (item.insertValue || item.insertValue === 0)) {
          this.totalOptionList[item.insertIndex - 1] = {
            value: item.insertValue,
            align: item.align || "right",
            showDecimal: item.showDecimal || false,
            preserveNumber: item.preserveNumber != null ? item.preserveNumber : 2,
            isHide: item.isHide || false,
          };
        }
      });
    }
  }

  currentPageDataChange($event: any): void {
    this.listOfDisplayData = $event;
    this.refreshStatus();
    if (!this.isCrossPageSelectLocal) {
      this.resetStatus();
    }
    this.scheduleSyncHorizontalScroll();
  }

  resetStatus() {
    this.isAllDisplayDataChecked = false;
    this.isIndeterminate = false;
    this.mapOfCheckedId = {};
    this.mapOfCheckList = {};
    this.selectEvent.emit([]);
  }

  refreshStatus(event: boolean = false, data: any = null): void {
    if (data) {
      if (event && this.isSelectDisabled(data)) {
        return;
      }
      this.mapOfCheckedId[data[this.selectField]] = event;
      if (event) {
        !this.mapOfCheckList[data[this.selectField]] &&
          (this.mapOfCheckList[data[this.selectField]] = data);
      } else {
        this.mapOfCheckList[data[this.selectField]] &&
          delete this.mapOfCheckList[data[this.selectField]];
      }
    }
    const sourceList =
      this.listOfDisplayData && this.listOfDisplayData.length
        ? this.listOfDisplayData
        : this.displayLines;
    const selectableList = (sourceList || []).filter(
      (item) => !this.isSelectDisabled(item),
    );
    this.isAllDisplayDataChecked =
      selectableList.length > 0 &&
      selectableList.every((item) => this.mapOfCheckedId[item[this.selectField]]);
    this.isIndeterminate =
      selectableList.some(
        (item) => this.mapOfCheckedId[item[this.selectField]],
      ) && !this.isAllDisplayDataChecked;
    if (this.selectEvent) {
      this.selectEvent.emit(Object.values(this.mapOfCheckList));
    }
  }

  checkAll(value: boolean): void {
    const sourceList =
      this.listOfDisplayData && this.listOfDisplayData.length
        ? this.listOfDisplayData
        : this.displayLines;
    (sourceList || []).forEach((item) => {
      if (this.isSelectDisabled(item)) {
        return;
      }
      if (value) {
        !this.mapOfCheckList[item[this.selectField]] &&
          (this.mapOfCheckList[item[this.selectField]] = item);
      } else {
        this.mapOfCheckList[item[this.selectField]] &&
          delete this.mapOfCheckList[item[this.selectField]];
      }
      this.mapOfCheckedId[item[this.selectField]] = value;
    });
    this.refreshStatus();
  }

  /** 当前行是否禁用勾选 */
  isSelectDisabled(data: any): boolean {
    return !!(
      this.selectDisabledField &&
      data &&
      data[this.selectDisabledField] === true
    );
  }

  /** 当前行是否失效 */
  isInvalidRow(data: any): boolean {
    return !!(
      this.rowInvalidField &&
      data &&
      data[this.rowInvalidField] === true
    );
  }
}
